import { skipToken } from "@reduxjs/toolkit/query";
import type {
  AccountGradingRecording,
  GradingId,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import { useEffect, useState, type FC, type Ref, type RefObject } from "react";
import { twMerge } from "tailwind-merge";
import { usePlaybookGradingCallOutlineQuery } from "../../api/entities/performancePlaybook.js";
import { useRecordingToolActivitiesQuery } from "../../api/entities/recording.js";
import { AudioPlayer } from "../../components/audioPlayer/audioPlayer.js";
import { CallOutlineNavigationPopover } from "../teammatePerformance/callOutlineNavigationPopover.js";
import type { QuoteMatchMap } from "../teammatePerformance/gradeValidationHelpers.js";
import {
  GradingTranscriptWithIndex,
  type GradingTranscriptWithIndexHandle,
} from "../teammatePerformance/gradingTranscriptWithIndex.js";
import { TranscriptVideoPanel } from "../teammatePerformance/transcriptVideoPanel.js";
import { useTranscriptOutlineSync } from "../teammatePerformance/useTranscriptOutlineSync.js";

interface TranscriptViewerPaneProps {
  gradingId: GradingId;
  sortedRecordings: readonly AccountGradingRecording[];
  quoteMatches: QuoteMatchMap;
  transcriptRef: Ref<GradingTranscriptWithIndexHandle>;
  transcriptContainerRef: RefObject<HTMLDivElement | null>;
}

/**
 * Reusable right-pane transcript viewer with call outline navigation,
 * evidence highlighting, and an audio player.
 *
 * Used by the KB adherence statement slideover, signal match slideover,
 * and customer interaction marker slideover.
 */
export const TranscriptViewerPane: FC<TranscriptViewerPaneProps> = ({
  gradingId,
  sortedRecordings,
  quoteMatches,
  transcriptRef,
  transcriptContainerRef,
}) => {
  const hasTranscript = sortedRecordings.some((r) => r.transcript);

  // Fetch tool activities for all recordings
  const recordingIds = sortedRecordings.map((r) => r.id);
  const { data: toolActivitiesByRecordingId } = useRecordingToolActivitiesQuery(
    recordingIds.length > 0 ? recordingIds : skipToken,
  );

  const { callPosition, scrollToCallPosition } = useTranscriptOutlineSync({
    transcriptContainerRef,
    sortedRecordings,
  });

  // Fetch call outline for overlay navigation
  const {
    data: callOutline,
    isLoading: isCallOutlineLoading,
    isError: isCallOutlineError,
  } = usePlaybookGradingCallOutlineQuery({ gradingId });

  const [hasScrolledTranscript, setHasScrolledTranscript] = useState(false);

  useEffect(() => {
    const container = transcriptContainerRef.current;
    if (!container) return;

    const handleScroll = (): void => {
      setHasScrolledTranscript(container.scrollTop > 0);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [transcriptContainerRef]);

  return (
    <>
      {/* Transcript pane */}
      <div className="flex flex-col bg-white">
        <div
          className={twMerge(
            "space-y-2 border-b border-transparent p-4",
            hasScrolledTranscript ? "border-gray-100" : "",
          )}
        >
          <h3 className="text-sm font-semibold text-foreground">
            Call transcript
          </h3>
          <CallOutlineNavigationPopover
            callOutline={callOutline}
            isLoading={isCallOutlineLoading}
            isError={isCallOutlineError}
            callPosition={callPosition}
            onCallPositionRequest={scrollToCallPosition}
          />
        </div>
        <TranscriptVideoPanel recordings={sortedRecordings} />
        <div
          ref={transcriptContainerRef}
          className="relative flex-1 overflow-y-auto px-4"
        >
          <div className="pb-4">
            {hasTranscript ? (
              <GradingTranscriptWithIndex
                ref={transcriptRef}
                recordings={sortedRecordings}
                quoteMatches={quoteMatches}
                scrollToCallPosition={scrollToCallPosition}
                toolActivitiesByRecordingId={toolActivitiesByRecordingId}
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                No transcript available
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Audio player spans full width */}
      <div className="col-span-full">
        <AudioPlayer />
      </div>
    </>
  );
};
